import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    Airplane, ArrowSquareOut, ArrowsClockwise, Bed, Calendar, ChatCircleDots, CheckCircle,
    CookingPot, ForkKnife, House, Link as LinkIcon, MagnifyingGlass, MapPin, MapTrifold,
    Question, ShieldWarning, Spinner, Suitcase, Train, TreePalm, User, Warning, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// kind → icon. Keep small + obvious.
const KIND_ICON = {
    hotel: Bed,
    transfer: MapTrifold,
    flight: Airplane,
    train: Train,
    activity: TreePalm,
    food: ForkKnife,
    other: Suitcase,
};

function relativeMinutes(iso) {
    if (!iso) return null;
    const diff = (Date.now() - new Date(iso).getTime()) / 60000;
    if (diff < 1) return "ahora mismo";
    if (diff < 60) return `hace ${Math.round(diff)} min`;
    if (diff < 60 * 24) return `hace ${Math.round(diff / 60)} h`;
    return `hace ${Math.round(diff / 1440)} d`;
}

function ReservationRow({ r, sofiBase }) {
    const Icon = KIND_ICON[r.kind] || Suitcase;
    const reservaUrl = `${sofiBase}/reservas/form/3/${r.reservation_id}`;
    return (
        <li className="group flex items-start gap-2.5 py-2 px-2 -mx-2 rounded-md hover:bg-slate-50 transition-colors"
            data-testid={`sofi-reservation-${r.reservation_id}`}>
            <Icon size={16} weight="duotone" className="mt-0.5 shrink-0 text-slate-600" />
            <div className="flex-1 min-w-0 text-xs">
                <div className="font-medium text-slate-800 truncate">{r.service || "—"}</div>
                <div className="text-slate-500 truncate">
                    {r.city ? `${r.city} · ` : ""}{r.start || "—"}{r.end && r.end !== r.start ? ` → ${r.end}` : ""}{r.time ? ` · ${r.time}` : ""}
                </div>
                {r.proveedor && r.proveedor !== "-" && (
                    <div className="text-slate-400 truncate">{r.proveedor}</div>
                )}
            </div>
            <a href={reservaUrl} target="_blank" rel="noopener noreferrer"
               className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] uppercase tracking-wider
                          font-bold text-brand-600 hover:text-brand-700 shrink-0 self-center px-1.5 py-1"
               data-testid={`sofi-reservation-open-${r.reservation_id}`}>
                Ver
            </a>
        </li>
    );
}

function MismatchWarningBanner({ warning, sofiUrl, dataTestid = "sofi-mismatch-warning" }) {
    // `warning` is trip_data.mismatch_warning from GET /sofi-data.
    // The banner is INTENTIONALLY non-dismissible — an agent seeing this
    // must not click it away and continue answering the customer based on
    // wrong data. The whole point is to force verification in SOFI.
    if (!warning?.detected) return null;
    const {
        discarded_count: n,
        expected_trip_id: tripId,
        expected_trip_lead_name: sofiLead,
        customer_name_hub: hubName,
    } = warning;
    return (
        <div
            data-testid={dataTestid}
            className="rounded-lg bg-red-50 border border-red-200 border-l-4 border-l-red-500
                       p-4 space-y-2 text-red-900"
            role="alert"
        >
            <div className="flex items-start gap-2">
                <Warning size={20} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold leading-tight">
                        Las reservas mostradas pueden no corresponder a este cliente
                    </div>
                    <div className="text-xs mt-1.5 leading-snug">
                        Se descartaron {n} reserva{n === 1 ? "" : "s"} por no coincidir con
                        el trip esperado{tripId ? ` (ID ${tripId})` : ""}
                        {sofiLead ? ` — SOFI reporta lead “${sofiLead}”` : ""}
                        {hubName ? ` frente al cliente “${hubName}” del Hub` : ""}.
                        <br />
                        <span className="font-semibold">Verificá directamente en SOFI</span> antes
                        de responder al cliente.
                    </div>
                </div>
            </div>
            {sofiUrl && (
                <div className="pl-7">
                    <a href={sofiUrl} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                  bg-red-600 hover:bg-red-700 text-white text-xs font-semibold
                                  transition-colors"
                       data-testid="sofi-mismatch-open-btn">
                        <ArrowSquareOut size={13} weight="bold" /> Abrir en SOFI
                    </a>
                </div>
            )}
        </div>
    );
}


function MismatchEmptyState({ warning, sofiUrl }) {
    // Edge case: EVERY row SOFI returned was discarded → the panel has
    // nothing to show. Distinct copy from the generic "sin reservas
    // registradas" because the operator needs to understand this is a
    // filter reject, not "the customer legitimately has no bookings".
    const n = warning?.discarded_count || 0;
    const tripId = warning?.expected_trip_id;
    return (
        <div
            data-testid="sofi-mismatch-empty"
            className="rounded-lg bg-red-50 border border-red-200 border-l-4 border-l-red-500
                       p-4 space-y-2 text-red-900"
            role="alert"
        >
            <div className="flex items-start gap-2">
                <Warning size={20} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold leading-tight">
                        No se encontraron reservas para este cliente en SOFI
                    </div>
                    <div className="text-xs mt-1.5 leading-snug">
                        El sistema descartó {n} reserva{n === 1 ? "" : "s"} que no coincidían
                        con el trip esperado{tripId ? ` (ID ${tripId})` : ""}. Podría ser que
                        el cliente aún no tenga reservas registradas, o que haya un error de
                        vinculación con SOFI.
                        <br />
                        <span className="font-semibold">Verificá directamente en SOFI</span>.
                    </div>
                </div>
            </div>
            {sofiUrl && (
                <div className="pl-7">
                    <a href={sofiUrl} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                  bg-red-600 hover:bg-red-700 text-white text-xs font-semibold
                                  transition-colors"
                       data-testid="sofi-mismatch-empty-open-btn">
                        <ArrowSquareOut size={13} weight="bold" /> Abrir en SOFI
                    </a>
                </div>
            )}
        </div>
    );
}


function MatchedState({ link, tripData, sofiBase, onRefresh, refreshing, onUnlink }) {
    const trip = tripData?.trip || {};
    const reservations = tripData?.reservations || [];
    const mismatchWarning = tripData?.mismatch_warning;
    // New canonical entry point requested by Eduardo: open the reservations list
    // filtered by trip_id (with session filters reset) instead of the trip form.
    const tripUrl = `${sofiBase}/reservas/list/3?app_bookings___trip_id_raw=${link.trip_id}&limitstart3=0&resetfilters=1&fabrik_incsessionfilters=0`;
    const fetchedAt = tripData?.fetched_at;
    const counts = trip.reservations_counts || {};

    // If the defensive filter dropped rows AND left us with nothing, replace
    // the whole reservations block with a distinctive empty state — otherwise
    // an operator would just see "Sin reservas registradas" which is misleading.
    const showMismatchEmpty =
        mismatchWarning?.detected && reservations.length === 0;

    return (
        <div className="space-y-3" data-testid="sofi-matched">
            {/* Sofi row-vs-trip mismatch banner. Renders BEFORE the trip
                summary so agents see it first — data integrity > cosmetics. */}
            <MismatchWarningBanner warning={mismatchWarning} sofiUrl={tripUrl} />

            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-2">
                <div className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">
                        Trip vinculado · #{link.trip_id}
                    </div>
                </div>
                <div className="space-y-1.5 text-xs text-slate-700">
                    {trip.destination && (
                        <div className="flex items-center gap-1.5"><MapPin size={12} weight="fill" />{trip.destination}</div>
                    )}
                    {(trip.start_date || trip.end_date) && (
                        <div className="flex items-center gap-1.5"><Calendar size={12} weight="fill" />
                            {trip.start_date}{trip.end_date ? ` → ${trip.end_date}` : ""}
                            {trip.nights ? ` · ${trip.nights} noches` : ""}
                        </div>
                    )}
                    {trip.agent && (
                        <div className="flex items-center gap-1.5"><User size={12} weight="fill" />Agente: {trip.agent}</div>
                    )}
                    {trip.status && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-slate-500">Estado:</span>
                            <Badge variant="outline" className="text-[10px] font-normal py-0">{trip.status}</Badge>
                            {trip.source && <Badge variant="secondary" className="text-[10px] font-normal py-0">{trip.source}</Badge>}
                        </div>
                    )}
                </div>
                <div className="pt-1 flex items-center gap-2">
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <a href={tripUrl} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700
                                              text-white text-xs font-semibold transition-colors"
                                   data-testid="sofi-open-trip-btn">
                                    <ArrowSquareOut size={13} weight="bold" /> Abrir en SOFI
                                </a>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">Abre el listado de reservas filtrado por este trip</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <Button variant="ghost" size="sm" className="px-2 h-8"
                            onClick={onRefresh} disabled={refreshing}
                            data-testid="sofi-refresh-btn">
                        <ArrowsClockwise size={13} weight="bold" className={refreshing ? "animate-spin" : ""} />
                    </Button>
                </div>
                <div className="flex items-center justify-between pt-1 text-[10px] text-slate-500">
                    <span data-testid="sofi-synced-at">Sincronizado {relativeMinutes(fetchedAt) || "—"}</span>
                    <button onClick={onUnlink} className="text-slate-400 hover:text-rose-600 underline-offset-2 hover:underline transition-colors"
                            data-testid="sofi-unlink-btn">desvincular</button>
                </div>
            </div>

            {/* Reservations */}
            <div data-testid="sofi-reservations-section">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
                        Reservas ({reservations.length})
                    </div>
                    {counts.total > 0 && (
                        <div className="text-[10px] text-slate-400">
                            {counts.confirmed > 0 && <span className="text-emerald-700">{counts.confirmed} OK · </span>}
                            {counts.new > 0 && <span>{counts.new} nuevas</span>}
                        </div>
                    )}
                </div>
                {showMismatchEmpty ? (
                    <MismatchEmptyState warning={mismatchWarning} sofiUrl={tripUrl} />
                ) : reservations.length === 0 ? (
                    <div className="text-xs text-slate-400 italic py-2">Sin reservas registradas</div>
                ) : (
                    // Cap height so the Acciones section below remains reachable
                    // without scrolling the whole aside when there are many bookings.
                    <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto vh-scroll pr-1" data-testid="sofi-reservations-list">
                        {reservations.map(r => (
                            <ReservationRow key={r.reservation_id} r={r} sofiBase={sofiBase} />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function AmbiguousState({ link, onPickCandidate, onSearch }) {
    return (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="sofi-ambiguous">
            <div className="flex items-start gap-2">
                <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900">
                    Varias coincidencias para <span className="font-semibold">&quot;{link.search_query}&quot;</span>
                </div>
            </div>
            <ul className="space-y-1.5">
                {(link.candidates || []).map(c => (
                    <li key={c.trip_id} className="flex items-center gap-2 p-2 rounded bg-white border border-amber-100">
                        <div className="flex-1 min-w-0 text-xs">
                            <div className="font-medium text-slate-800 truncate">{c.lead_name || "—"}</div>
                            <div className="text-slate-500 truncate">
                                #{c.trip_id} · {c.start_date || "—"} → {c.end_date || "—"}
                                {c.status ? ` · ${c.status}` : ""}
                            </div>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => onPickCandidate(c.trip_id)}
                                data-testid={`sofi-pick-candidate-${c.trip_id}`}>
                            Vincular
                        </Button>
                    </li>
                ))}
            </ul>
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onSearch}
                    data-testid="sofi-search-other-btn">
                <MagnifyingGlass size={12} weight="bold" className="mr-1" /> Buscar otro
            </Button>
        </div>
    );
}

function UnmatchedState({ link, onSearch }) {
    return (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="sofi-unmatched">
            <div className="flex items-start gap-2">
                <Question size={16} weight="duotone" className="text-slate-500 mt-0.5 shrink-0" />
                <div className="text-xs text-slate-600">
                    No se encontró trip en SOFI {link?.search_query ? <>para <span className="font-semibold">&quot;{link.search_query}&quot;</span></> : "para este cliente"}.
                </div>
            </div>
            <Button variant="outline" size="sm" className="w-full text-xs" onClick={onSearch}
                    data-testid="sofi-manual-search-btn">
                <MagnifyingGlass size={12} weight="bold" className="mr-1" /> Buscar manualmente
            </Button>
        </div>
    );
}

function ErrorState({ link, onRetry }) {
    return (
        <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 space-y-2" data-testid="sofi-error">
            <div className="flex items-start gap-2">
                <ShieldWarning size={16} weight="fill" className="text-rose-600 mt-0.5 shrink-0" />
                <div className="text-xs text-rose-900">
                    Información SOFI no disponible
                    {link?.error && <div className="text-[10px] text-rose-700 mt-1 font-mono break-all">{link.error.slice(0, 120)}</div>}
                </div>
            </div>
            <Button variant="outline" size="sm" className="w-full text-xs" onClick={onRetry}
                    data-testid="sofi-retry-btn">
                <ArrowsClockwise size={12} weight="bold" className="mr-1" /> Reintentar
            </Button>
        </div>
    );
}

function NotApplicableState({ link }) {
    return (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="sofi-not-applicable">
            <div className="text-xs text-slate-500">
                SOFI no aplicable para este canal{link?.reason ? `: ${link.reason}` : ""}.
            </div>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-2 animate-pulse" data-testid="sofi-loading">
            <div className="flex items-center gap-2 text-xs text-slate-500">
                <Spinner size={14} className="animate-spin" /> Buscando en SOFI…
            </div>
            <div className="h-3 bg-slate-200 rounded w-3/4" />
            <div className="h-3 bg-slate-200 rounded w-1/2" />
            <div className="h-3 bg-slate-200 rounded w-2/3" />
        </div>
    );
}

function SofiSearchDialog({ open, onOpenChange, defaultQuery, onLink }) {
    const [q, setQ] = useState(defaultQuery || "");
    const [tripIdInput, setTripIdInput] = useState("");
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setQ(defaultQuery || "");
            setTripIdInput("");
            setItems(null);
            setError(null);
        }
    }, [open, defaultQuery]);

    const doSearch = useCallback(async (e) => {
        if (e) e.preventDefault();
        if (!q.trim() || q.trim().length < 2) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/sofi/search`, { params: { name: q.trim() } });
            if (!data.ok) setError(data.error || "Error en SOFI");
            setItems(data.items || []);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [q]);

    const doLink = useCallback(async (trip_id) => {
        setSubmitting(true);
        try {
            await onLink(trip_id);
            onOpenChange(false);
        } catch (e) {
            toast.error("Error al vincular: " + (e?.response?.data?.detail || e.message));
        } finally {
            setSubmitting(false);
        }
    }, [onLink, onOpenChange]);

    const doLinkById = useCallback(async () => {
        const n = parseInt(tripIdInput, 10);
        if (!n || n < 1) {
            toast.error("Introduce un ID numérico válido");
            return;
        }
        await doLink(n);
    }, [tripIdInput, doLink]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="sofi-search-dialog">
                <DialogHeader>
                    <DialogTitle>Vincular trip de SOFI</DialogTitle>
                </DialogHeader>

                <form onSubmit={doSearch} className="space-y-2">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        Buscar por nombre de cliente
                    </label>
                    <div className="flex gap-2">
                        <Input value={q} onChange={(e) => setQ(e.target.value)}
                               placeholder="Eduardo Moratalla"
                               data-testid="sofi-search-input" autoFocus />
                        <Button type="submit" size="sm" disabled={loading || q.trim().length < 2}
                                data-testid="sofi-search-submit">
                            {loading ? <Spinner size={14} className="animate-spin" /> : <MagnifyingGlass size={14} weight="bold" />}
                        </Button>
                    </div>
                </form>

                {error && (
                    <div className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded p-2">
                        {error}
                    </div>
                )}

                {items !== null && (
                    <div className="border-t border-slate-100 pt-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                            Resultados ({items.length})
                        </div>
                        {items.length === 0 ? (
                            <div className="text-xs text-slate-500 italic py-2">
                                Sin coincidencias. Prueba con el apellido o vincula por ID abajo.
                            </div>
                        ) : (
                            <ul className="max-h-60 overflow-y-auto divide-y divide-slate-100">
                                {items.map(c => (
                                    <li key={c.trip_id} className="flex items-center gap-2 py-2"
                                        data-testid={`sofi-search-result-${c.trip_id}`}>
                                        <div className="flex-1 min-w-0 text-xs">
                                            <div className="font-medium text-slate-800 truncate">{c.lead_name || "—"}</div>
                                            <div className="text-slate-500 truncate">
                                                #{c.trip_id} · {c.start_date || "—"} → {c.end_date || "—"}
                                            </div>
                                        </div>
                                        <Button size="sm" variant="outline" className="h-7 text-xs"
                                                disabled={submitting}
                                                onClick={() => doLink(c.trip_id)}
                                                data-testid={`sofi-search-pick-${c.trip_id}`}>
                                            Vincular
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div className="border-t border-slate-100 pt-2 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        ¿Conoces el ID del trip?
                    </div>
                    <div className="flex gap-2">
                        <Input type="number" placeholder="2271" value={tripIdInput}
                               onChange={(e) => setTripIdInput(e.target.value)}
                               data-testid="sofi-link-by-id-input" />
                        <Button size="sm" variant="default" onClick={doLinkById} disabled={submitting || !tripIdInput}
                                data-testid="sofi-link-by-id-btn">
                            <LinkIcon size={13} weight="bold" />
                        </Button>
                    </div>
                    <div className="text-[10px] text-slate-400">
                        Lo verás en la URL del trip en SOFI: <span className="font-mono">/trips/form/1/<span className="text-slate-600">2271</span></span>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="sofi-search-close">
                        Cerrar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function SofiPanel({ conversation, customer }) {
    const [data, setData] = useState(null);       // { link, trip_data, sofi_base }
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    const convId = conversation?._id || conversation?.id;
    const channel = conversation?.channel;

    const load = useCallback(async () => {
        if (!convId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/conversations/${convId}/sofi-data`);
            setData(data);
        } catch (e) {
            setData({ link: { status: "error", error: e?.response?.data?.detail || e.message } });
        } finally {
            setLoading(false);
        }
    }, [convId]);

    useEffect(() => { load(); }, [load]);

    const refresh = useCallback(async () => {
        if (!convId) return;
        setRefreshing(true);
        try {
            const { data } = await api.post(`/conversations/${convId}/sofi-refresh`);
            setData(prev => ({ ...(prev || {}), ...data }));
            toast.success("Sincronizado con SOFI");
        } catch (e) {
            toast.error("No se pudo sincronizar: " + (e?.response?.data?.detail || e.message));
        } finally {
            setRefreshing(false);
        }
    }, [convId]);

    const linkTo = useCallback(async (trip_id) => {
        const { data } = await api.post(`/conversations/${convId}/sofi-link`, { trip_id });
        setData(prev => ({ ...(prev || {}), ...data }));
        toast.success(`Trip #${trip_id} vinculado`);
    }, [convId]);

    const unlink = useCallback(async () => {
        try {
            await api.post(`/conversations/${convId}/sofi-unlink`);
            setData(prev => ({ ...(prev || {}), link: null, trip_data: null }));
            toast.success("Desvinculado");
        } catch (e) {
            toast.error("No se pudo desvincular: " + (e?.response?.data?.detail || e.message));
        }
    }, [convId]);

    const link = data?.link;
    const sofiBase = data?.sofi_base || "https://gestion.viajadverdad.com";

    return (
        <section data-testid="sofi-panel">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
                    SOFI · Información del viaje
                </div>
                {link?.status === "matched" && (
                    <Badge variant="secondary" className="text-[9px] font-normal py-0">
                        #{link.trip_id}
                    </Badge>
                )}
            </div>

            {loading && <LoadingState />}

            {!loading && (!link || link.status === "unmatched") && (
                <UnmatchedState link={link} onSearch={() => setSearchOpen(true)} />
            )}
            {!loading && link?.status === "ambiguous" && (
                <AmbiguousState link={link} onPickCandidate={linkTo} onSearch={() => setSearchOpen(true)} />
            )}
            {!loading && link?.status === "matched" && (
                <MatchedState
                    link={link}
                    tripData={data?.trip_data}
                    sofiBase={sofiBase}
                    onRefresh={refresh}
                    refreshing={refreshing}
                    onUnlink={unlink}
                />
            )}
            {!loading && link?.status === "error" && (
                <ErrorState link={link} onRetry={refresh} />
            )}
            {!loading && link?.status === "not_applicable" && (
                <NotApplicableState link={link} />
            )}

            <SofiSearchDialog
                open={searchOpen}
                onOpenChange={setSearchOpen}
                defaultQuery={customer?.name || link?.search_query || ""}
                onLink={linkTo}
            />
        </section>
    );
}
